import CryptoJS from 'crypto-js';
import dayjs from 'dayjs';
import { v4 } from 'uuid';

import { getUserId } from '@/login/login';
import { customReport, CustomReportType } from '@/report/tam';
import { CgiErrorCode } from '@/services/cgi';
import { checkResourceExist } from '@/services/story';
import { getCryptoPwd } from '@/utils/crypto';

import { ISpineResult } from './folder';

export interface ICosData {
  cosUrl?: string;
  spineData?: ISpineResult;
}

export interface HashFile {
  hash: string;
  file: File;
}

export interface ExistedFileInfo {
  hash: string;
  url?: string;
  spine?: ISpineResult;
};

interface UploadMatchResult {
  hashFiles?: HashFile[];
  existedFiles?: ExistedFileInfo[];
  toUploadFiles?: HashFile[];
  folderName?: string;
  folderHash?: string;
};

const CHUNK_SIZE = 1024 * 1024 * 20;

/**
 * 计算文件SHA256哈希值
 * @param file 要计算哈希的文件对象
 * @return Promise<string> 返回对应的哈希值
 */
export const getFileHash = (file: File): Promise<string> => new Promise((resolve, reject) => {
  const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
  let currentChunk = 0;
  const hash = CryptoJS.algo.SHA256.create(); // 创建一个 SHA256 哈希对象

  const processNextChunk = () => {
    const start = currentChunk * CHUNK_SIZE;
    const end = Math.min(start + CHUNK_SIZE, file.size);
    const blob = file.slice(start, end); // 切割文件成小块
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const arrayBuffer = e.target?.result as ArrayBuffer;
        const wordArray = CryptoJS.lib.WordArray.create(arrayBuffer);
        hash.update(wordArray); // 将当前块的哈希值更新到总哈希中
        currentChunk += 1;
        if (currentChunk < totalChunks) {
          // 继续读取下一个块
          processNextChunk();
        } else {
          // 读取完所有块后计算最终的哈希值
          const finalHash = hash.finalize().toString();
          const nameParts = file.name?.split('.');
          const suffix = nameParts.length > 1
            ? nameParts.pop()!.toLowerCase()
            : '';
          const hashStr = suffix ? `${finalHash}.${suffix}` : finalHash;
          resolve(hashStr);
        }
      } catch (err) {
        reject(new Error(`计算哈希失败: ${err}`));
      }
    };

    reader.onerror = (err) => {
      reject(new Error(`文件读取失败: ${err}`));
    };

    reader.readAsArrayBuffer(blob); // 读取当前块
  };

  processNextChunk(); // 启动第一次读取
});

/**
 * 查询已存在的文件信息
 * @param hashes 需要查询的哈希值数组
 * @param isFolder 是否为文件夹
 * @param storyId（可选）故事ID，按照故事维度去重
 * @return Promise<ExistedFileInfo[]> 返回已存在文件的信息数组
 */
const checkExistedFiles = async (
  hashes: string[],
  isFolder: boolean,
  storyId?: string,
): Promise<ExistedFileInfo[]> => {
  if (!hashes.length) return [];

  try {
    // 对于spine文件夹，如果存在，可以直接返回spine数据
    if (isFolder) {
      const combinedHash = hashes.sort().join('');
      const folderHash = CryptoJS.SHA256(combinedHash).toString();
      if (!storyId) {
        return [{
          hash: folderHash,
          spine: undefined,
        }];
      }

      const { code, data, message } = await checkResourceExist(storyId || '', folderHash);
      if (code !== CgiErrorCode.NO_ERROR) {
        console.error('文件夹资源查重失败', message);
        return [{
          hash: folderHash,
          spine: undefined,
        }];
      }
      return [{
        hash: folderHash,
        spine: data?.spineData,
      }];
    }


    if (!storyId) return [];
    const checkPromises = hashes.map(hash => checkResourceExist(storyId || '', hash)
      .then(res => ({
        isExisted: res.code === CgiErrorCode.NO_ERROR && !!res.data?.cosUrl,
        hash,
        url: res.data?.cosUrl,
      }))
      .catch((err) => {
        console.error('资源查重失败:', err);
        return { isExisted: false, hash, url: '' };
      }));

    const results = await Promise.all(checkPromises);
    return results
      .filter(e => e.isExisted)
      .map(({ hash, url }) => ({
        url,
        hash,
      }));
  } catch (error) {
    console.error('查询已存在的文件失败:', error);
    return [];
  }
};

/**
 * 查询已存在文件，确认待上传新文件
 * @param files 文件数组
 * @param storyId（可选）故事ID，按照故事维度去重
 * @returns Promise<UploadMatchResult> 返回匹配校验结果
 */
export const uploadFileMatch = async (files: File[], storyId?: string): Promise<UploadMatchResult> => {
  if (!files.length) {
    return {
      hashFiles: [],
      existedFiles: [],
      toUploadFiles: [],
    };
  }

  // 计算所有文件哈希并去重
  const uniqueHashMap = new Map<string, File>();
  const hashFiles: HashFile[] = await Promise.all(files.map(async (file) => {
    try {
      const hash = await getFileHash(file);
      if (!uniqueHashMap.has(hash)) {
        uniqueHashMap.set(hash, file);
      }
      return { hash, file };
    } catch (err) {
      console.error(file.name, err);
      const hash = getCryptoPwd(`${v4()}-${dayjs().valueOf()}-${file.name}`);
      if (!uniqueHashMap.has(hash)) {
        uniqueHashMap.set(hash, file);
      }

      customReport(CustomReportType.UPLOAD_FILE, {
        ['上报类型']: '上传文件计算hash失败',
        user: getUserId(),
        hash,
        name: file.name,
        type: file.type,
        size: file.size / 1024 / 1024,
      });
      return { hash, file };
    }
  }));


  // 查询已存在文件
  const existedFiles = await checkExistedFiles([...uniqueHashMap.keys()], false, storyId);
  const existedHashes = new Set(existedFiles.map(f => f.hash));

  // 待上传新文件
  const toUploadFiles: HashFile[] = Array.from(uniqueHashMap.entries())
    .filter(([hash]) => !existedHashes.has(hash))
    .map(([hash, file]) => ({ hash, file }));

  return {
    hashFiles,
    existedFiles,
    toUploadFiles,
  };
};

/**
 * 匹配 Spine文件夹
 * @param files 文件数组
 * @param storyId（可选）故事ID，按照故事维度去重
 * @returns Promise<UploadMatchResult> 返回匹配校验结果
 */
export const uploadSpineMatch = async (files: File[], storyId?: string): Promise<UploadMatchResult> => {
  if (!files.length) {
    return {
      existedFiles: [],
      folderName: '',
      folderHash: '',
    };
  }

  // 计算所有文件哈希并去重
  const hashFiles: HashFile[] = await Promise.all(files.map(async (f) => {
    try {
      const hash = await getFileHash(f);
      return { hash, file: f };
    } catch (err) {
      console.error(f.name, err);
      const hash = getCryptoPwd(`${v4()}-${dayjs().valueOf()}-${f.name}`);

      customReport(CustomReportType.UPLOAD_FILE, {
        ['上报类型']: '上传Spine计算hash失败',
        user: getUserId(),
        hash,
        name: f.name,
        type: f.type,
        size: f.size / 1024 / 1024,
      });
      return { hash, file: f };
    }
  }));
  const uniqueHashes = [...new Set(hashFiles.map(f => f.hash))];

  // 查询已存在文件夹
  const existedFiles = await checkExistedFiles(uniqueHashes, true, storyId);

  // 待上传新文件夹
  const toUploadFiles = existedFiles[0]?.spine ? [] : hashFiles;

  return {
    existedFiles,
    toUploadFiles,
    folderName: getFolderName(files[0]),
    folderHash: existedFiles[0]?.hash || '',
  };
};

// 获取文件夹名
export const getFolderName = (file: File) => {
  if (file.webkitRelativePath) {
    const parts = file.webkitRelativePath.split('/');
    return parts.length > 1 ? parts[0] : '';
  }
  return '';
};
